import type {
  AgentCustomSqlCloudConfig,
  AgentCustomSqlCloudSchedule,
  AgentCustomSqlDestination,
  AgentCustomSqlRowCountCondition,
  AgentCustomSqlTestConfigChange,
  AgentCustomSqlTestConfigFields,
  AgentCustomSqlTestCreateChange,
  AgentTestOperation,
  FieldChange,
} from '../types';
import { isAgentCustomSqlTestCreateChange } from '../types';
import { pluralizeString } from './common';

const GT_ROW_COUNT = /^>\s*(\d+)$/;
const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const DEFAULT_CUSTOM_SQL_CLOUD_SCHEDULE: AgentCustomSqlCloudSchedule = {
  type: 'daily',
  hour: 9,
  minute: 0,
};

export const DEFAULT_CUSTOM_SQL_CLOUD_CONFIG: AgentCustomSqlCloudConfig = {
  fail_row_count_threshold: 0,
  warn_row_count_threshold: null,
  store_row_sample: true,
  sample_row_limit: 100,
  query_timeout_seconds: 300,
};

const isFieldChange = (value: unknown): value is FieldChange<unknown> =>
  typeof value === 'object' && value != null && 'new' in value;

const extractFieldNew = (value: unknown): unknown =>
  isFieldChange(value) ? value.new : value;

const asFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const asBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  return null;
};

export const parseCustomSqlGtRowCount = (value: unknown): number | null => {
  const raw = extractFieldNew(value);
  if (typeof raw !== 'string') return null;
  const match = raw.trim().match(GT_ROW_COUNT);
  return match ? Number(match[1]) : null;
};

const firstParsedThreshold = (
  ...values: Array<AgentCustomSqlRowCountCondition | undefined>
): number | null => {
  for (const value of values) {
    const parsed = parseCustomSqlGtRowCount(value);
    if (parsed != null) return parsed;
  }
  return null;
};

const isScheduleType = (
  value: string,
): value is AgentCustomSqlCloudSchedule['type'] =>
  value === 'every_x_hours' ||
  value === 'daily' ||
  value === 'weekly' ||
  value === 'custom';

const scheduleFromType = (
  type: Exclude<AgentCustomSqlCloudSchedule['type'], 'custom'>,
): AgentCustomSqlCloudSchedule => {
  if (type === 'every_x_hours') {
    return { type, interval_hours: 6 };
  }
  if (type === 'weekly') {
    return { type, day_of_week: 1, hour: 9, minute: 0 };
  }
  return { type: 'daily', hour: 9, minute: 0 };
};

export const isFiveFieldCron = (value?: string | null): boolean =>
  (value?.trim().split(/\s+/) ?? []).length === 5;

export const parseCustomSqlCronSchedule = (
  cron: string,
): AgentCustomSqlCloudSchedule | null => {
  if (!isFiveFieldCron(cron)) return null;
  const parts = cron.trim().split(/\s+/);
  const [minutePart, hourPart, dayOfMonth, month, dayOfWeekPart] = parts;
  if (dayOfMonth !== '*' || month !== '*') return null;
  const minute = asFiniteNumber(minutePart);
  const hour = asFiniteNumber(hourPart);
  if (minute == null || hour == null) return null;
  if (dayOfWeekPart === '*') {
    return { type: 'daily', hour, minute };
  }
  const dayOfWeek = asFiniteNumber(dayOfWeekPart);
  if (dayOfWeek == null) return null;
  return {
    type: 'weekly',
    day_of_week: dayOfWeek === 7 ? 0 : dayOfWeek,
    hour,
    minute,
  };
};

export const parseCustomSqlSchedule = (
  value: unknown,
): AgentCustomSqlCloudSchedule | null => {
  const extracted = extractFieldNew(value);
  if (extracted == null) return null;

  if (typeof extracted === 'string') {
    const cron = parseCustomSqlCronSchedule(extracted);
    if (cron) return cron;
    const trimmed = extracted.trim();
    if (isFiveFieldCron(trimmed)) {
      return { type: 'custom', cron: trimmed };
    }
    const type = trimmed.toLowerCase();
    return isScheduleType(type) && type !== 'custom'
      ? scheduleFromType(type)
      : null;
  }

  if (typeof extracted !== 'object') return null;
  const record = extracted as Record<string, unknown>;
  const type =
    typeof record.type === 'string' && isScheduleType(record.type)
      ? record.type
      : null;

  if (!type) {
    return typeof record.cron === 'string'
      ? parseCustomSqlSchedule(record.cron)
      : null;
  }

  if (type === 'custom') {
    const expression =
      typeof record.cron === 'string' ? record.cron.trim() : '';
    return expression ? { type: 'custom', cron: expression } : null;
  }

  if (type === 'every_x_hours') {
    return {
      type,
      interval_hours:
        asFiniteNumber(record.interval_hours ?? record.intervalHours) || 6,
    };
  }

  const hour = asFiniteNumber(record.hour) ?? 9;
  const minute = asFiniteNumber(record.minute) ?? 0;
  if (type === 'weekly') {
    return {
      type,
      day_of_week: asFiniteNumber(record.day_of_week ?? record.dayOfWeek) ?? 1,
      hour,
      minute,
    };
  }
  return { type: 'daily', hour, minute };
};

export const getCustomSqlScheduleFromChange = (
  change: AgentCustomSqlTestConfigChange,
): AgentCustomSqlCloudSchedule | null => {
  if (!isAgentCustomSqlTestCreateChange(change)) return null;
  for (const value of [
    change.customSqlSchedule,
    change.cadence,
    change.schedule,
    change.cron,
  ]) {
    const parsed = parseCustomSqlSchedule(value);
    if (parsed) return parsed;
  }
  return null;
};

const formatScheduleTime = (hour: number, minute: number): string => {
  const period = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
};

export const formatCustomSqlCloudSchedule = (
  schedule: AgentCustomSqlCloudSchedule,
): string => {
  if (schedule.type === 'custom') {
    return `Custom (${schedule.cron})`;
  }
  if (schedule.type === 'every_x_hours') {
    return `Every ${pluralizeString(schedule.interval_hours, 'hour', 'hours')}`;
  }
  const timeLabel = formatScheduleTime(schedule.hour, schedule.minute ?? 0);
  if (schedule.type === 'weekly') {
    const dayLabel =
      DAY_LABELS[schedule.day_of_week] ?? `Day ${schedule.day_of_week}`;
    return `Weekly on ${dayLabel} at ${timeLabel}`;
  }
  return `Daily at ${timeLabel}`;
};

const readConfigRecord = (
  config: AgentCustomSqlCloudConfig | undefined,
): Record<string, unknown> =>
  config && typeof config === 'object'
    ? (config as unknown as Record<string, unknown>)
    : {};

const firstNumber = (
  record: Record<string, unknown>,
  ...keys: string[]
): number | null => {
  for (const key of keys) {
    const parsed = asFiniteNumber(record[key]);
    if (parsed != null) return parsed;
  }
  return null;
};

const firstBoolean = (
  record: Record<string, unknown>,
  ...keys: string[]
): boolean | null => {
  for (const key of keys) {
    const parsed = asBoolean(record[key]);
    if (parsed != null) return parsed;
  }
  return null;
};

const normalizeCustomSqlCloudConfig = (
  change: AgentCustomSqlTestCreateChange,
): AgentCustomSqlCloudConfig => {
  const existing = readConfigRecord(change.customSqlConfig);
  const topLevel = change as unknown as Record<string, unknown>;
  const failFromConfig = firstNumber(
    existing,
    'fail_row_count_threshold',
    'failRowCountThreshold',
  );
  const failRowCount =
    failFromConfig != null && failFromConfig > 0
      ? failFromConfig
      : firstParsedThreshold(change.errorIf, change.error_if);
  const warnFromConfig = firstNumber(
    existing,
    'warn_row_count_threshold',
    'warnRowCountThreshold',
  );
  const warnRowCount =
    warnFromConfig ?? firstParsedThreshold(change.warnIf, change.warn_if);
  const storeRowSample =
    firstBoolean(existing, 'store_row_sample', 'storeRowSample') ??
    firstBoolean(topLevel, 'store_row_sample', 'storeRowSample');
  const sampleRowLimit =
    firstNumber(existing, 'sample_row_limit', 'sampleRowLimit') ??
    firstNumber(topLevel, 'sample_row_limit', 'sampleRowLimit');
  const queryTimeoutSeconds =
    firstNumber(existing, 'query_timeout_seconds', 'queryTimeoutSeconds') ??
    firstNumber(topLevel, 'query_timeout_seconds', 'queryTimeoutSeconds');
  const qualityDimension =
    (typeof existing.quality_dimension === 'string'
      ? existing.quality_dimension
      : undefined) ??
    (typeof existing.qualityDimension === 'string'
      ? existing.qualityDimension
      : undefined);

  return {
    fail_row_count_threshold:
      failRowCount ?? DEFAULT_CUSTOM_SQL_CLOUD_CONFIG.fail_row_count_threshold,
    warn_row_count_threshold:
      warnRowCount ?? DEFAULT_CUSTOM_SQL_CLOUD_CONFIG.warn_row_count_threshold,
    store_row_sample:
      storeRowSample ?? DEFAULT_CUSTOM_SQL_CLOUD_CONFIG.store_row_sample,
    sample_row_limit:
      sampleRowLimit ?? DEFAULT_CUSTOM_SQL_CLOUD_CONFIG.sample_row_limit,
    query_timeout_seconds:
      queryTimeoutSeconds ??
      DEFAULT_CUSTOM_SQL_CLOUD_CONFIG.query_timeout_seconds,
    quality_dimension: qualityDimension,
  };
};

const hasCustomSqlConfigSignal = (
  change: AgentCustomSqlTestCreateChange,
  config: AgentCustomSqlCloudConfig,
): boolean => {
  const topLevel = change as unknown as Record<string, unknown>;
  return (
    change.customSqlConfig != null ||
    config.fail_row_count_threshold > 0 ||
    config.warn_row_count_threshold != null ||
    topLevel.store_row_sample != null ||
    topLevel.storeRowSample != null ||
    topLevel.sample_row_limit != null ||
    topLevel.sampleRowLimit != null ||
    topLevel.query_timeout_seconds != null ||
    topLevel.queryTimeoutSeconds != null
  );
};

const normalizeCustomSqlCreateChange = (
  change: AgentCustomSqlTestCreateChange,
): AgentCustomSqlTestCreateChange => {
  const customSqlSchedule = getCustomSqlScheduleFromChange(change);
  const customSqlConfig = normalizeCustomSqlCloudConfig(change);
  return {
    ...change,
    ...(customSqlSchedule ? { customSqlSchedule } : {}),
    ...(hasCustomSqlConfigSignal(change, customSqlConfig)
      ? { customSqlConfig }
      : {}),
  };
};

export const getCustomSqlFailRowCount = (
  change: AgentCustomSqlTestConfigChange,
): number | null => {
  if (!isAgentCustomSqlTestCreateChange(change)) return null;
  const threshold = change.customSqlConfig?.fail_row_count_threshold;
  return threshold && threshold > 0 ? threshold : null;
};

export const getCustomSqlWarnRowCount = (
  change: AgentCustomSqlTestConfigChange,
): number | null => {
  if (!isAgentCustomSqlTestCreateChange(change)) return null;
  return change.customSqlConfig?.warn_row_count_threshold ?? null;
};

export const getCustomSqlStoreRowSample = (
  change: AgentCustomSqlTestConfigChange,
): boolean | null => {
  if (!isAgentCustomSqlTestCreateChange(change)) return null;
  return change.customSqlConfig?.store_row_sample ?? null;
};

export const getCustomSqlSampleRowLimit = (
  change: AgentCustomSqlTestConfigChange,
): number | null => {
  if (!isAgentCustomSqlTestCreateChange(change)) return null;
  return change.customSqlConfig?.sample_row_limit ?? null;
};

export const getCustomSqlQueryTimeoutSeconds = (
  change: AgentCustomSqlTestConfigChange,
): number | null => {
  if (!isAgentCustomSqlTestCreateChange(change)) return null;
  return change.customSqlConfig?.query_timeout_seconds ?? null;
};

export const formatCustomSqlRowCountCondition = (threshold: number): string =>
  `> ${threshold}`;

// A custom SQL test being created has no row identity of its own — it is a file
// whose name the wizard can rename — so stamp a positional id and a rename can't
// silently re-include a row the user deselected. Deletes are left alone: their
// preview rows come from the tests table and are keyed by test id.
export const withCustomSqlChangeIds = (
  changes: AgentCustomSqlTestConfigChange[],
): AgentCustomSqlTestConfigChange[] =>
  changes.map((change, index) => {
    if (!isAgentCustomSqlTestCreateChange(change)) return change;
    return normalizeCustomSqlCreateChange({
      ...change,
      changeId: change.changeId || `custom-sql-${index}`,
    });
  });

export const formatCustomSqlTestConfigFieldsSummary = (
  fields: AgentCustomSqlTestConfigFields,
  change?: AgentCustomSqlTestConfigChange,
): string => {
  const parts: string[] = [];

  if (fields.severity) parts.push(`severity: ${fields.severity.new ?? 'none'}`);
  const failRowCount = change ? getCustomSqlFailRowCount(change) : null;
  const warnRowCount = change ? getCustomSqlWarnRowCount(change) : null;
  if (failRowCount != null) {
    parts.push(`error if ${formatCustomSqlRowCountCondition(failRowCount)}`);
  }
  if (warnRowCount != null) {
    parts.push(`warn if ${formatCustomSqlRowCountCondition(warnRowCount)}`);
  }
  const schedule = change ? getCustomSqlScheduleFromChange(change) : null;
  if (schedule) {
    parts.push(`cadence: ${formatCustomSqlCloudSchedule(schedule)}`);
  }
  const storeRowSample = change ? getCustomSqlStoreRowSample(change) : null;
  if (storeRowSample === false) {
    parts.push('no row sample');
  }
  const sampleRowLimit = change ? getCustomSqlSampleRowLimit(change) : null;
  if (
    sampleRowLimit != null &&
    sampleRowLimit !== DEFAULT_CUSTOM_SQL_CLOUD_CONFIG.sample_row_limit
  ) {
    parts.push(`sample limit: ${sampleRowLimit}`);
  }
  const queryTimeout = change ? getCustomSqlQueryTimeoutSeconds(change) : null;
  if (
    queryTimeout != null &&
    queryTimeout !== DEFAULT_CUSTOM_SQL_CLOUD_CONFIG.query_timeout_seconds
  ) {
    parts.push(`timeout: ${queryTimeout}s`);
  }
  if (fields.description) parts.push('description');
  if (fields.tags) parts.push(`tags: ${fields.tags.new?.length ?? 0}`);
  if (fields.owners) parts.push(`owners: ${fields.owners.new?.length ?? 0}`);
  if (fields.qualityDimension) parts.push('quality dimension');

  return parts.join(', ') || 'no metadata';
};

export const formatCustomSqlTestChangeSummary = (
  change: AgentCustomSqlTestConfigChange,
  operation: AgentTestOperation,
): string => {
  if (operation === 'delete') return 'delete custom SQL test';
  return `Create custom SQL test — ${formatCustomSqlTestConfigFieldsSummary(
    change,
    change,
  )}`;
};

export const formatCustomSqlTestChangeLabel = (
  change: AgentCustomSqlTestConfigChange,
): string =>
  isAgentCustomSqlTestCreateChange(change)
    ? change.name
    : change.testName || change.testId;

export const resolveCustomSqlDestination = (
  change: AgentCustomSqlTestConfigChange,
  isCloudEnabled: boolean,
): AgentCustomSqlDestination => {
  if (
    isAgentCustomSqlTestCreateChange(change) &&
    (change.destination === 'cloud' || change.destination === 'pr')
  ) {
    return change.destination;
  }
  return isCloudEnabled ? 'cloud' : 'pr';
};

export const getCustomSqlPlanActionLabel = (
  changes: AgentCustomSqlTestConfigChange[],
  isCloudEnabled: boolean,
): string => {
  const destinations = changes
    .filter(isAgentCustomSqlTestCreateChange)
    .map((change) => resolveCustomSqlDestination(change, isCloudEnabled));
  if (destinations.length === 0) return 'Open PR';
  const hasCloud = destinations.includes('cloud');
  const hasPr = destinations.includes('pr');
  if (hasCloud && hasPr) return 'Apply';
  if (hasCloud) return 'Create';
  return 'Open PR';
};

export const getCustomSqlSubmitLabel = (
  changes: AgentCustomSqlTestConfigChange[],
  selectedCount: number,
  isCloudEnabled: boolean,
): string => {
  const action = getCustomSqlPlanActionLabel(changes, isCloudEnabled);
  if (action === 'Create') {
    return `Create ${pluralizeString(selectedCount, 'test', 'tests')}`;
  }
  return action;
};
